import { baseApi } from '../baseApi';
import {
  cacheKey,
  cacheTenantId,
  pruneCatalogCache,
  readCachedQuery,
  writeCachedQuery,
} from './catalogCache';

/**
 * The catalog and the categories — the one payload an operator waits on before
 * they can take an order, cached so they do not wait on it twice.
 *
 * ## What is cached, and where
 *
 * Two layers, and they answer different questions:
 *
 * - **RTK Query's in-memory cache** makes the second mount in a session free.
 *   Opening the till dialog for the tenth table costs nothing.
 * - **IndexedDB** (`./catalogCache`) makes the first mount *after a reload*
 *   free, which is the one that actually hurts: `GET /catalog?limit=200` is
 *   ~100 KB of JSON for a 167-item demo menu, and a venue's own is larger.
 *
 * The read path is cache-first, network-behind: {@link listFullCatalog} answers
 * from disk if disk has anything, paints, and revalidates in the background,
 * replacing the rows through `upsertQueryData` when the network answers. If the
 * network fails it falls back to disk again — so a till that lost its uplink
 * mid-shift keeps its menu instead of emptying its grid.
 *
 * ## What a cached menu may and may not be used for
 *
 * **Prices here are display-only.** §38: the client shows what it read, the
 * server prices what it charges, and `POST /orders` never carries a price. A
 * three-day-old `basePrice` on a tile is a label, not a promise.
 *
 * **A cached menu will offer something the kitchen cannot make.** Sold-out
 * flags and menu schedule windows both move without the client hearing about
 * it, and both are enforced server-side — `order-pricing.ts` raises 422 for a
 * sold-out item and `assertMenuWindowsOpen` raises 422 for one outside its
 * window. That 422 is not a bug in the cache; it is the cache working, and the
 * UI has to read it as an ordinary answer ("not available right now") rather
 * than as a failure. `QuickOrderDialog` does exactly that.
 */

/** The base query's error shape, mirrored from `baseApi`'s `axiosBaseQuery`. */
export interface CatalogApiError {
  status?: number;
  message: string;
}

interface QueryResult {
  data?: unknown;
  error?: unknown;
}

/** `baseQuery` as RTK Query hands it to a `queryFn`. */
type Fetcher = (arg: {
  url: string;
  params?: Record<string, unknown>;
}) => QueryResult | Promise<QueryResult>;

export interface CategoryRow {
  id: string;
  name: string;
  parentId: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CatalogItemRow {
  id: string;
  name: string;
  /** The API's fixed-scale decimal string. Never `Number()` it for money — pass
   *  it to `useCurrency().format`, and use minor units for any arithmetic. */
  basePrice: string;
  sku: string | null;
  categoryId: string | null;
  type?: string;
  imageUrl?: string | null;
  isSoldOut?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  /** §25 — `CatalogService.findAll` includes these; `localizedName` reads them. */
  translations?: { locale: string; name: string; description?: string | null }[];
  /**
   * What the list read already counts (`_count: { variants, modifierGroups }`).
   *
   * Declared because an ordering screen has a question that needs it: a product
   * with sizes or modifier groups has to be *configured* before it becomes a
   * cart line, and the counts are how a tile knows to ask rather than merging a
   * large and a small into one line. The groups themselves are not in this
   * payload — `GET /catalog/:id` is what carries them, and it is read on demand.
   *
   * Optional because a row is still usable without it, and an older API build
   * may not send it; a missing count is read as "ask anyway", never as "no
   * choices", because the cheap mistake is one extra dialog and the expensive
   * one is a wrong line.
   */
  _count?: { variants: number; modifierGroups: number } | null;
}

/**
 * What every endpoint here resolves to.
 *
 * The paginated envelope is unwrapped once, at the edge, so no screen repeats
 * `rowsOf(...)`; `fetchedAt` and `fromCache` are what let the till say "this is
 * the menu saved on this device" instead of pretending a week-old grid is live.
 */
export interface CachedList<T> {
  rows: T[];
  total: number;
  /** Epoch ms at which these rows came back from the API. */
  fetchedAt: number;
  /** True while these rows are the disk copy — the network answer is still in
   *  flight, or it failed and this is the fallback. */
  fromCache: boolean;
}

export interface CategoriesQueryArgs {
  limit?: number;
  isActive?: boolean;
  search?: string;
}

/** {@link listFullCatalog} deliberately takes no `search`: it holds the whole
 *  menu, so searching it is a client-side filter over rows already in hand. */
export interface FullCatalogQueryArgs {
  isActive?: boolean;
  categoryId?: string;
}

/** `PaginationDto` caps `limit` at 200, so the sweep pages at exactly that. */
const PAGE_SIZE = 200;

/** A stop, not a policy: 5 000 items is far past any real venue menu, and an
 *  API that kept reporting more must not spin the till forever. */
const MAX_PAGES = 25;

/** Endpoints whose result is written to disk. A query carrying a `search` term
 *  is never one of them — a cached search result is worth nothing on the next
 *  reload and would only crowd the store. */
type PersistedEndpoint = 'listCategories' | 'listFullCatalog';

/**
 * Arg keys the network has already answered in this tab.
 *
 * A refetch — `refetchOnReconnect`, `refetchOnMountOrArgChange`, or the explicit
 * one the till fires after a 422 — must go to the network, not quietly re-serve
 * the disk copy it has already superseded. Only a cold start reads disk first.
 */
const servedFresh = new Set<string>();

/**
 * Re-publishes a background revalidation into the RTK Query cache.
 *
 * Assigned after `catalogApi` exists rather than closed over inside it: a
 * `queryFn` that referenced `catalogApi.util` from within its own initializer
 * would make the slice's type circular.
 */
type Publisher = (dispatch: (action: any) => unknown, args: any, value: any) => void;
const publishers: Partial<Record<PersistedEndpoint, Publisher>> = {};

/** Unwraps the API's `{ data, meta }` envelope; tolerates a bare array. */
function unwrap<T>(payload: unknown): { rows: T[]; total: number } {
  if (Array.isArray(payload)) return { rows: payload as T[], total: payload.length };
  const body = payload as { data?: T[]; meta?: { total?: number } } | null;
  const rows = body?.data ?? [];
  return { rows, total: body?.meta?.total ?? rows.length };
}

function asError(error: unknown): CatalogApiError {
  const shaped = error as CatalogApiError | undefined;
  return { status: shaped?.status, message: shaped?.message ?? 'Request failed' };
}

/**
 * Drops `undefined` before the params reach axios.
 *
 * The API's global `ValidationPipe` runs with `forbidNonWhitelisted`, so an
 * undeclared or empty query param is a 400 rather than a no-op. Every key below
 * exists on `QueryCatalogDto` / `QueryCategoryDto`.
 */
function params(source: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  }
  return out;
}

async function fetchOnePage<T>(
  fetcher: Fetcher,
  url: string,
  query: Record<string, unknown>,
): Promise<{ rows: T[]; total: number } | { error: CatalogApiError }> {
  const result = await fetcher({ url, params: params(query) });
  if (result.error) return { error: asError(result.error) };
  return unwrap<T>(result.data);
}

/**
 * Every page of `/catalog`, concatenated.
 *
 * The till filters and searches in memory, which is only honest if it holds the
 * whole menu — a 201-item venue whose grid silently stopped at 200 would hide
 * real products from the operator. On a first load this is one request per 200
 * items; on every load after that it is zero, because the result goes to disk.
 */
async function sweepCatalog(
  fetcher: Fetcher,
  args: FullCatalogQueryArgs,
): Promise<{ value: CachedList<CatalogItemRow> } | { error: CatalogApiError }> {
  const rows: CatalogItemRow[] = [];
  let total = 0;

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await fetchOnePage<CatalogItemRow>(fetcher, '/catalog', {
      page,
      limit: PAGE_SIZE,
      isActive: args.isActive,
      categoryId: args.categoryId,
    });
    if ('error' in result) return { error: result.error };

    rows.push(...result.rows);
    total = result.total;
    if (result.rows.length === 0 || rows.length >= total) break;
  }

  return { value: { rows, total, fetchedAt: Date.now(), fromCache: false } };
}

/**
 * The cache-first read, shared by both persisted endpoints.
 *
 * Order of preference: disk on a cold start, then network; network on a
 * refetch, then disk if the network refuses. The background revalidation that
 * follows a disk hit is deliberately unawaited — the whole point is that the
 * grid paints before it finishes.
 */
async function readThroughCache<T>(
  endpointName: PersistedEndpoint,
  args: unknown,
  dispatch: (action: any) => unknown,
  fetchFresh: () => Promise<{ value: CachedList<T> } | { error: CatalogApiError }>,
): Promise<{ data: CachedList<T> } | { error: CatalogApiError }> {
  const tenantId = cacheTenantId();
  const key = tenantId ? cacheKey(tenantId, endpointName, args) : null;

  const persist = async (value: CachedList<T>) => {
    if (key && tenantId) await writeCachedQuery(key, tenantId, endpointName, value);
  };

  if (key && !servedFresh.has(key)) {
    const record = await readCachedQuery<CachedList<T>>(key);
    if (record?.payload?.rows) {
      const cached: CachedList<T> = { ...record.payload, fromCache: true };

      void (async () => {
        const fresh = await fetchFresh();
        if ('error' in fresh) return;
        servedFresh.add(key);
        await persist(fresh.value);
        publishers[endpointName]?.(dispatch, args, fresh.value);
      })();

      return { data: cached };
    }
  }

  const fresh = await fetchFresh();
  if ('error' in fresh) {
    // Offline, or the API is down. A stale menu beats an empty grid: the
    // operator can still compose the order, and the server will refuse
    // anything that has since gone away when it is sent.
    if (key) {
      const record = await readCachedQuery<CachedList<T>>(key);
      if (record?.payload?.rows) return { data: { ...record.payload, fromCache: true } };
    }
    return { error: fresh.error };
  }

  if (key) servedFresh.add(key);
  await persist(fresh.value);
  return { data: fresh.value };
}

export const catalogApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * `GET /categories` — the filter chips above the item grid.
     *
     * Small (~6 KB) but on the critical path: without it the grid has no
     * categories to filter by, so it is cached on the same terms as the menu.
     */
    listCategories: build.query<CachedList<CategoryRow>, CategoriesQueryArgs | void>({
      queryFn: async (rawArgs, api, _extraOptions, baseQuery) => {
        const args: CategoriesQueryArgs = rawArgs ?? {};
        const fetcher = baseQuery as Fetcher;

        const fetchFresh = async () => {
          const result = await fetchOnePage<CategoryRow>(fetcher, '/categories', {
            limit: args.limit ?? 100,
            isActive: args.isActive,
            search: args.search,
          });
          if ('error' in result) return result;
          return {
            value: {
              rows: result.rows,
              total: result.total,
              fetchedAt: Date.now(),
              fromCache: false,
            },
          };
        };

        // A search is a live question, not a cacheable answer.
        if (args.search) {
          const fresh = await fetchFresh();
          return 'error' in fresh ? { error: fresh.error } : { data: fresh.value };
        }
        return readThroughCache('listCategories', args, api.dispatch, fetchFresh);
      },
      providesTags: (result) => [
        { type: 'Category' as const, id: 'LIST' },
        ...(result?.rows ?? []).map((row) => ({ type: 'Category' as const, id: row.id })),
      ],
    }),

    /**
     * `GET /catalog`, swept to the last page and written to disk — the till's
     * menu.
     *
     * This is the endpoint the ordering screens should use. It costs one
     * request per 200 items the first time and nothing at all afterwards, and
     * it lets category filtering and search happen in memory, so tapping a
     * category or typing a letter is instant instead of being a round trip the
     * operator watches.
     */
    listFullCatalog: build.query<CachedList<CatalogItemRow>, FullCatalogQueryArgs | void>({
      queryFn: async (rawArgs, api, _extraOptions, baseQuery) => {
        const args: FullCatalogQueryArgs = rawArgs ?? {};
        const fetcher = baseQuery as Fetcher;
        return readThroughCache('listFullCatalog', args, api.dispatch, () =>
          sweepCatalog(fetcher, args),
        );
      },
      providesTags: (result) => [
        { type: 'Catalog' as const, id: 'LIST' },
        ...(result?.rows ?? []).map((row) => ({ type: 'Catalog' as const, id: row.id })),
      ],
    }),
  }),
});

publishers.listCategories = (dispatch, args, value) =>
  dispatch(catalogApi.util.upsertQueryData('listCategories', args, value));
publishers.listFullCatalog = (dispatch, args, value) =>
  dispatch(catalogApi.util.upsertQueryData('listFullCatalog', args, value));

// Housekeeping, once, when the slice loads: expired records and any left behind
// by a different tenant on this device. Fire-and-forget — nothing waits on it.
void pruneCatalogCache(cacheTenantId());

export const { useListCategoriesQuery, useListFullCatalogQuery } = catalogApi;

/**
 * Someone renamed, moved, added or deleted a category.
 *
 * `CategoryTree` writes through axios, so nothing invalidated this before and
 * `listCategories` is one of the two endpoints persisted to disk — the till's
 * category chips could lag a rename by a reload rather than by a refetch.
 * The read-through cache serves the network answer once the tab has had one,
 * so invalidating here really does re-ask the server.
 */
export const invalidateCategories = () =>
  catalogApi.util.invalidateTags([{ type: 'Category' as const, id: 'LIST' }]);
